import React, { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FormControl, Grid, InputLabel, Stack } from '@mui/material'
import {
  AddButton,
  PuiSelect,
  PuiTextArea,
  type Question,
  type QuestionAnswer,
  type SelectEntity,
} from '@pbt/pbt-ui-components'

import type { AnswerInputType } from '~/api/graphql/generated/types'

import AnswersTable from './AnswersTable'

interface MultipleSelectionOptionProps {
  buttonText: string
  disableAnswer: boolean
  displayOptions: AnswerInputType['answerInputSubTypes']
  hasMultipleSelectionAnswers: boolean
  inputIcon?: ReactNode
  isFollowUpQuestion?: boolean
  onAddAnswerRequested: () => void
  onDelete?: (answer: QuestionAnswer) => void
  onDisplayOptionChange: (option: string) => void
  onEdit: (answer: QuestionAnswer, index?: number) => void
  onOrderChange: (oldIndex: number, newIndex: number) => void
  placeholder: string
  questionCandidate: Question
}

const MultipleSelectionOptions: React.FC<MultipleSelectionOptionProps> = ({
  buttonText,
  disableAnswer,
  displayOptions,
  hasMultipleSelectionAnswers,
  inputIcon,
  isFollowUpQuestion,
  questionCandidate,
  onDisplayOptionChange,
  onDelete,
  onEdit,
  onOrderChange,
  onAddAnswerRequested,
  placeholder,
}) => {
  const { t } = useTranslation(['Admin', 'Common'])

  return (
    <Grid container flexDirection="column" pb={2} pl={4.5} pt={1}>
      <Grid container item>
        <Grid item pb={2} xs={5}>
          <FormControl fullWidth>
            <InputLabel>
              {t('Admin:CATALOG.QUESTION.DISPLAY_OPTIONS_AS')}
            </InputLabel>
            <PuiSelect
              disabled={disableAnswer}
              items={(displayOptions || []) as SelectEntity[]}
              renderEmpty={false}
              value={questionCandidate?.answerInputSubtypeId || ''}
              onChange={(event) =>
                onDisplayOptionChange(event.target.value as string)
              }
            />
          </FormControl>
        </Grid>
      </Grid>

      {hasMultipleSelectionAnswers && !isFollowUpQuestion && (
        <Grid item ml={-4.5} pb={2}>
          <AnswersTable
            answers={questionCandidate.answers!}
            isDragDisabled={disableAnswer}
            onDelete={disableAnswer ? undefined : onDelete}
            onEdit={disableAnswer ? undefined : onEdit}
            onOrderChange={onOrderChange}
          />
        </Grid>
      )}

      {isFollowUpQuestion && hasMultipleSelectionAnswers && (
        <Grid item data-testid="question-input" mb={1}>
          {questionCandidate.answers.map((answer, index) => (
            <Stack
              direction="row"
              key={answer.id}
              spacing={1}
              sx={{ alignItems: 'center', mb: 1 }}
            >
              {inputIcon}
              <PuiTextArea
                minRows={1}
                placeholder={placeholder}
                value={answer.text}
                onChange={({ target: { value } }) =>
                  onEdit(
                    { text: value, inputs: [] } as unknown as QuestionAnswer,
                    index,
                  )
                }
              />
            </Stack>
          ))}
        </Grid>
      )}

      <Grid item>
        <AddButton
          addText={buttonText}
          disabled={disableAnswer}
          onAdd={onAddAnswerRequested}
        />
      </Grid>
    </Grid>
  )
}

export default MultipleSelectionOptions
